import { useState, useEffect } from 'react';
import { PlayIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import type { YouTubeVideo } from '@mermaids/shared';
import YouTubeVideoCard from './YouTubeVideoCard';
import YouTubeModal from './YouTubeModal';
import { API_ENDPOINTS } from '../../config/constants';
import { isValidYouTubeVideoId, YOUTUBE_CHANNEL_URL } from './utils';

const YouTubeSection = () => {
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchTravelVideos();
  }, []);

  const fetchTravelVideos = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.youtube}/travel-videos?maxResults=6`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data.success)
        throw new Error(data.error || 'Failed to fetch videos');
      setVideos(data.data || []);
    } catch {
      setError('Unable to load videos at the moment');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (videoId: string) => {
    // Always play inside modal; placeholders redirect only
    if (videoId.includes('placeholder')) {
      window.open(YOUTUBE_CHANNEL_URL, '_blank');
      return;
    }

    if (!isValidYouTubeVideoId(videoId)) {
      return;
    }

    setSelectedVideoId(videoId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVideoId(null);
  };

  if (loading) {
    return (
      <section className="py-12 lg:py-16 bg-[#faf6f1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4 animate-pulse">
              <div className="h-8 w-64 bg-stone-200 rounded-lg"></div>
            </div>
            <div className="h-5 w-80 bg-stone-200 rounded-lg mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-stone-200 overflow-hidden animate-pulse shadow-sm"
              >
                <div className="aspect-video bg-stone-200"></div>
                <div className="p-5">
                  <div className="h-5 bg-stone-200 rounded mb-3"></div>
                  <div className="h-4 bg-stone-200 rounded mb-2"></div>
                  <div className="h-4 bg-stone-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-12 lg:py-16 bg-[#faf6f1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              Videos on YouTube
            </h2>
            <p className="text-stone-500 max-w-2xl mx-auto">
              Watch our latest vlogs, destination guides, and behind-the-scenes
              content
            </p>
          </div>

          {error && (
            <div className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-white border border-stone-200 rounded-lg text-stone-500 text-sm shadow-sm">
                <span>Videos loading with sample content</span>
              </div>
            </div>
          )}

          {/* Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {videos.map((video, index) => (
              <YouTubeVideoCard
                key={video.id}
                video={video}
                index={index}
                onVideoClick={handleVideoClick}
              />
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-5 p-6 bg-stone-900 rounded-2xl text-white shadow-xl">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold mb-1">
                  Subscribe for More
                </h3>
                <p className="text-sm text-stone-400">
                  Join us for weekly content
                </p>
              </div>
              <a
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                <PlayIcon className="w-5 h-5 mr-2" />
                Subscribe Now
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Modal */}
      <YouTubeModal
        videoId={selectedVideoId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default YouTubeSection;
